import {
  HelpCircleIcon,
  LogOutIcon,
  XIcon,
} from 'lucide-react'
import { type ComponentProps, forwardRef, useState } from 'react'

import { useLogout } from 'components/hooks/useLogout'
import MemberOnboardingSupportContent from 'components/MemberOnboardingSupportContent'
import { DropdownMenu, Modal, ProfileMenuToggle } from 'slab'

import { useOnboarding } from '../OnboardingContext'
import { type Step } from '../types'

type ProfileMenuTriggerProps = Omit<
  ComponentProps<typeof ProfileMenuToggle>,
  'children'
> & {
  memberName?: string;
}

const ProfileMenuTrigger = forwardRef<
  HTMLButtonElement,
  ProfileMenuTriggerProps
>(({ width, memberName: _memberName, ...props }, forwardedRef) => {
  const { onboardingData } = useOnboarding()

  const memberOrEmployeeName = onboardingData
    ? onboardingData.member?.given_name && onboardingData.member?.family_name
      ? `${onboardingData.member.given_name} ${onboardingData.member.family_name}`
      : `${onboardingData.employee?.first_name} ${onboardingData.employee?.last_name}`
    : ''

  return (
    <ProfileMenuToggle
      {...props}
      data-testid='profile-menu-toggle'
      width={width}
      ref={forwardedRef}
    >
      {memberOrEmployeeName}
    </ProfileMenuToggle>
  )
})

const ProfileMenuTriggerWithoutContext = forwardRef<
  HTMLButtonElement,
  ProfileMenuTriggerProps
>(({ width, memberName, ...props }, forwardedRef) => {
  return (
    <ProfileMenuToggle
      {...props}
      data-testid='profile-menu-toggle-empty'
      width={width}
      ref={forwardedRef}
    >
      {memberName ?? ' '}
    </ProfileMenuToggle>
  )
})

export function ProfileMenu ({
  navigateToSpecificStep,
  showHelpAndSupport,
  showOptedOut,
  withoutContext,
  memberName,
  width,
}: {
  navigateToSpecificStep?: (step: Step) => void;
  showHelpAndSupport?: boolean;
  showOptedOut?: boolean;
  width?: 'full' | 'auto';
  withoutContext?: boolean;
  memberName?: string;
}) {
  const { mutate: logout } = useLogout()
  const [supportModalOpen, setSupportModalOpen] = useState(false)

  const Trigger = withoutContext ? ProfileMenuTriggerWithoutContext : ProfileMenuTrigger

  return (
    <>
      <DropdownMenu trigger={<Trigger memberName={memberName} width={width} />}>
        {showHelpAndSupport && (
          <DropdownMenu.Item
            leftIcon={HelpCircleIcon}
            onSelect={() => setSupportModalOpen(true)}
          >
            Help & support
          </DropdownMenu.Item>
        )}
        <DropdownMenu.Item onSelect={() => logout()} leftIcon={LogOutIcon}>
          Save and sign out
        </DropdownMenu.Item>
        {!withoutContext && showOptedOut && (
          <DropdownMenu.Section>
            <DropdownMenu.Item
              onSelect={
                navigateToSpecificStep
                  ? () => navigateToSpecificStep('opted_out')
                  : undefined
              }
              leftIcon={XIcon}
              type='destructive'
            >
              Decline coverage
            </DropdownMenu.Item>
          </DropdownMenu.Section>
        )}
      </DropdownMenu>
      {showHelpAndSupport && (
        <Modal open={supportModalOpen} onOpenChange={setSupportModalOpen}>
          <Modal.Body>
            <MemberOnboardingSupportContent
              onDismiss={() => setSupportModalOpen(false)}
            />
          </Modal.Body>
        </Modal>
      )}
    </>
  )
}
